import React from 'react';
import classnames from 'classnames';
import HfReactHelper from 'hellosign/components/hf-react-helper';
import { TooltipUI } from 'hellospa/foundation/control/tooltip';
import { FormattedMessage } from 'react-intl';
import Handle from '../handle';
import styles from './page.scss';
import PositionContext from '../position-context';
import ResilientImage from '../../resilient-image';

function useImgLoaded() {
  const [imgLoaded, setState] = React.useState(
    // Tests don't actually load the <img, so the onLoad will never fire
    navigator.userAgent.includes('jsdom'),
  );

  const handleLoad = React.useCallback(
    () => setState(true),
    [],
  );

  return [imgLoaded, handleLoad];
}

const NOOP = (tmp) => tmp;

/**
 * Handle page rotation and set appropriate styles for the doc images
 * @param rotationDegree
 * @param height
 * @param width
 * @param page
 */
function handlePageRotation(rotationDegree, height, width, page) {
  const imgStyle = { height, width };

  // if it's the default rotation degree, just return the default style
  if (rotationDegree === '0deg') {
    return imgStyle;
  }

  imgStyle.transform = `rotate(${rotationDegree})`;

  // if the orientations changed, apply styles below
  if (page.newOrientation !== page.orientation) {
    imgStyle.width = height;
    imgStyle.height = width;
    imgStyle.maxWidth = height;
    imgStyle.transformOrigin = 'top left';
    // change translate x & y between 90 & -90 degrees
    imgStyle.transform += (rotationDegree === '90deg')
      ? 'translate(0, -100%)'
      : 'translate(-100%, 0)';
  }

  return imgStyle;
}

function Page(props) {
  const {
    Field,
    wrapImg = NOOP,
    fields,
    margin,
    onPageClick = NOOP,
    page,
    pageIndex,
    showImg,
    width,
    className,
    rotationDegree = '0deg',
    showUneditableMessage,
  } = props;
  const pageRef = React.useRef();
  const imgStyle = handlePageRotation(rotationDegree, '100%', '100%', page);

  // Firefox drags the image instead of letting us handle drag
  const blockNativeDrag = React.useCallback(
    (e) => e.preventDefault(),
    [],
  );
  const [imgLoaded, handleLoad] = useImgLoaded();
  const onClick = React.useCallback(
    (e) => onPageClick(e, pageIndex),
    [onPageClick, pageIndex],
  );

  let useFallback = false;
  let src = HfReactHelper.attachSessionInfoToUrl(page.src);
  if (page.cdnSrc) {
    useFallback = true;
    src = page.cdnSrc;
  }
  // This padding and the .aspectContainer are able to force the page into the
  // specified aspect ratio. https://css-tricks.com/aspect-ratio-boxes/
  const paddingTop = `${page.height / page.width * parseFloat(width)}%`;
  return (
    <div
      id={`page-${pageIndex}`}
      data-qa-ref={
        props['data-qa-ref'] || `page-${pageIndex}`
      }
      className={classnames(styles.page, className, {
        [styles.imgHidden]: showImg === false,
      })}
      style={{ width, margin, paddingTop }}
      ref={pageRef}
    >
      <div className={styles.aspectContainer} >
        <PositionContext pageRef={pageRef}>
          {showImg ? wrapImg(
            <div><ResilientImage
              className={styles.img}
              style={imgStyle}
              draggable={false}
              onMouseDown={blockNativeDrag}
              onLoad={handleLoad}
              src={src}
              fallbackUrl={useFallback ? HfReactHelper.attachSessionInfoToUrl(page.src) : ''}
              onClick={onClick} /></div>,
          ) : null}
          {showUneditableMessage ? <TooltipUI
            className={styles.tooltip}
          >
            <FormattedMessage
              id=""
              description="On hover, this tooltip is displayed to explain the associated icon"
              defaultMessage={'Template cannot be edited.'} />
          </TooltipUI> : null}
          {(imgLoaded || showImg === false) && fields.map((f) => (
            <Handle
              key={f.id}
              fieldData={f}
              Field={Field} />
          ))}
        </PositionContext>
      </div>
    </div>
  );
}

export default React.memo(Page);
